import { useState, useEffect, useCallback, useRef } from 'react';
import { getExplorePosts, getFollowingPosts, getPopularPosts } from '../services/postService';

/**
 * Hook para manejar posts del feed
 * @param {string} filter - 'explore' | 'following' | 'popular'
 * @param {string} userId - ID del usuario (para 'following')
 * @returns {object}
 */
export const usePosts = (filter = 'explore', userId = null) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);

  const requestIdRef = useRef(0);
  const lastVisibleRef = useRef(lastVisible);
  useEffect(() => {
    lastVisibleRef.current = lastVisible;
  }, [lastVisible]);

  const loadPosts = useCallback(async (isLoadMore = false) => {
    const requestId = ++requestIdRef.current;
    try {
      if (!isLoadMore) {
        setLoading(true);
        setError(null);
      }

      let result;

      switch (filter) {
        case 'following':
          if (!userId) {
            setPosts([]);
            return;
          }
          result = await getFollowingPosts(userId, 50, isLoadMore ? lastVisibleRef.current : null);
          break;

        case 'popular':
          result = await getPopularPosts(50);
          break;

        case 'explore':
        default:
          result = await getExplorePosts(50, isLoadMore ? lastVisibleRef.current : null);
          break;
      }

      if (requestId !== requestIdRef.current) return; // stale

      if (result.success) {
        if (isLoadMore) {
          setPosts((prev) => [...prev, ...result.posts]);
        } else {
          setPosts(result.posts);
        }
        setLastVisible(result.lastVisible || null);
        setHasMore(result.hasMore || false);
      } else {
        setError(result.error);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return; // stale
      setError(err.message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [filter, userId]);

  const loadMore = () => {
    if (hasMore && !loading) {
      loadPosts(true);
    }
  };

  const refresh = () => {
    setLastVisible(null);
    setHasMore(true);
    loadPosts(false);
  };

  useEffect(() => {
    // reset cursor on feed change
    setLastVisible(null);
    setHasMore(true);
    loadPosts(false);
  }, [filter, userId]);

  return {
    posts,
    setPosts,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
};
